import translate from '@translate';
import React, { FC, useMemo } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import TextField from 'src/components/TextField';
import Select from 'src/components/Select';
import Checkbox from 'src/components/Checkbox';
import Typography from 'src/components/Typography';
import { WebhookRecordType } from 'src/types/webhook';
import { WEBHOOK_PARAMETERS, WebhookMethod, WebhookPayloadVersion, WebhookType } from 'skladon-types';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import colorOptionFce from 'src/utils/colorOptionFce';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EditKeysType } from './constants';
import { formatWebhookDateTime } from './functions';

export interface DetailPropsType {
    onChange: OnChangeType<EditKeysType>;
    editData: WebhookRecordType;
    editErrors: FormErrorsType;
    newWebhook: boolean;
    disableEdit: boolean;
    boolNumberCodes: ConstantsCodeType[];
}

const Detail: FC<DetailPropsType> = (props) => {
    const webhookTypeOptions = useMemo(
        () =>
            Object.values(WebhookType).map((value) => ({
                value,
                label: translate(`webhooks.webhookType.${value}`),
            })),
        []
    );

    const methodOptions = useMemo(
        () =>
            Object.values(WebhookMethod).map((value) => ({
                value,
                label: value,
            })),
        []
    );

    const payloadVersionOptions = useMemo(
        () =>
            Object.values(WebhookPayloadVersion).map((value) => ({
                value,
                label: translate(`webhooks.payloadVersion.${value}`),
            })),
        []
    );

    const showPackingListsParameter = WEBHOOK_PARAMETERS.withPackingLists.webhookTypes.includes(
        props.editData.WEBHOOK_TYPE as WebhookType
    );

    const handlePackingListsChange = (checked: boolean) => {
        props.onChange(
            'PARAMETERS',
            {
                ...(props.editData.PARAMETERS || {}),
                withPackingLists: checked,
            },
            false
        );
    };

    return (
        <Grid>
            <GridItem cols={3}>
                <Select
                    options={webhookTypeOptions}
                    value={props.editData.WEBHOOK_TYPE}
                    error={props.editErrors.WEBHOOK_TYPE}
                    onChange={(value, error) => props.onChange('WEBHOOK_TYPE', value, error)}
                    name="WEBHOOK_TYPE"
                    label={translate('webhookDetail.webhookType')}
                    fullWidth
                    required
                    disabled={props.disableEdit}
                />
            </GridItem>
            <GridItem cols={3}>
                <Select
                    options={methodOptions}
                    value={props.editData.METHOD || WebhookMethod.POST}
                    error={props.editErrors.METHOD}
                    onChange={(value, error) => props.onChange('METHOD', value, error)}
                    name="METHOD"
                    label={translate('webhookDetail.method')}
                    fullWidth
                    required
                    disabled={props.disableEdit}
                />
            </GridItem>
            <GridItem cols={3}>
                <Select
                    options={props.boolNumberCodes}
                    value={props.editData.ACTIVE}
                    error={props.editErrors.ACTIVE}
                    onChange={(value, error) => props.onChange('ACTIVE', value, error)}
                    name="ACTIVE"
                    label={translate('webhookDetail.active')}
                    fullWidth
                    required
                    disabled={props.disableEdit}
                    optionStyleFce={colorOptionFce}
                />
            </GridItem>
            <GridItem cols={3}>
                <Select
                    options={payloadVersionOptions}
                    value={props.editData.PAYLOAD_VERSION || WebhookPayloadVersion.V21}
                    error={props.editErrors.PAYLOAD_VERSION}
                    onChange={(value, error) => props.onChange('PAYLOAD_VERSION', value, error)}
                    name="PAYLOAD_VERSION"
                    label={translate('webhookDetail.payloadVersion')}
                    fullWidth
                    required
                    disabled={props.disableEdit}
                />
            </GridItem>
            <GridItem cols={12}>
                <TextField
                    value={props.editData.URL}
                    error={props.editErrors.URL}
                    onChange={(value, error) => props.onChange('URL', value, error)}
                    name="URL"
                    label={translate('webhookDetail.url')}
                    fullWidth
                    required
                    disabled={props.disableEdit}
                />
            </GridItem>
            {!props.newWebhook && (
                <>
                    <GridItem cols={3}>
                        <TextField
                            value={formatWebhookDateTime(props.editData.REGISTERED_AT)}
                            onChange={() => undefined}
                            name="REGISTERED_AT"
                            label={translate('webhookDetail.registeredAt')}
                            fullWidth
                            disabled
                        />
                    </GridItem>
                    <GridItem cols={3}>
                        <TextField
                            value={formatWebhookDateTime(props.editData.UNREGISTERED_AT)}
                            onChange={() => undefined}
                            name="UNREGISTERED_AT"
                            label={translate('webhookDetail.unregisteredAt')}
                            fullWidth
                            disabled
                        />
                    </GridItem>
                    <GridItem cols={3}>
                        <TextField
                            value={formatWebhookDateTime(props.editData.DEACTIVATION_EMAIL_SENT_AT)}
                            onChange={() => undefined}
                            name="DEACTIVATION_EMAIL_SENT_AT"
                            label={translate('webhookDetail.deactivationEmailSentAt')}
                            fullWidth
                            disabled
                        />
                    </GridItem>
                </>
            )}
            {showPackingListsParameter && (
                <GridItem cols={12}>
                    <Typography variant="subtitle2" gutterBottom>
                        {translate('webhookDetail.parameters')}
                    </Typography>
                    <Typography
                        component="label"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            cursor: props.disableEdit ? 'default' : 'pointer',
                            userSelect: 'none',
                        }}
                    >
                        <Checkbox
                            value={!!props.editData.PARAMETERS?.withPackingLists}
                            onChange={(value) => {
                                if (!props.disableEdit) {
                                    handlePackingListsChange(!!value);
                                }
                            }}
                        />
                        <Typography component="span" sx={{ marginLeft: '8px' }}>
                            {translate('webhookDetail.withPackingLists')}
                        </Typography>
                    </Typography>
                </GridItem>
            )}
        </Grid>
    );
};

export default Detail;
